import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { IconArrowLeft, IconPlus } from '@tabler/icons-react';
import {
  isTaskListReference,
  type TaskListDefinition,
  type TaskListReference,
  type TaskSectionDefinition,
} from '@techgitter/contracts';
import { SectionEditor } from './SectionEditor';
import { TaskListReferenceEditor } from '../admin/template-task-configs/TaskListReferenceEditor';
import { HelpHint } from '../admin/HelpHint';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../toolbar';
import { FormField } from '../panel/FormField';
import { CARD_SX, OUTLINED_BTN_SX } from '../../styles/designSystem';
import { slugify } from '../../utils/slugify';
import '../admin/EditorCard.css';

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } as const;

interface TaskListDetailEditorProps {
  taskList: TaskListDefinition;
  /**
   * The full catalog — needed to populate the reference picker's
   * options when adding a TaskListReference section.
   */
  taskLists?: TaskListDefinition[];
  onChange: (updated: TaskListDefinition) => void;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
}

export const TaskListDetailEditor: React.FC<TaskListDetailEditorProps> = ({
  taskList,
  taskLists = [],
  onChange,
  onBack,
  onSave,
  saving,
}) => {
  const [newSectionLabel, setNewSectionLabel] = useState('');

  // Exclude the current task list from the reference picker — you
  // can't reference yourself, and the resolver would warn on it.
  const referenceableTaskLists = taskLists.filter(
    tl => tl.taskListId !== taskList.taskListId,
  );

  const handleFieldChange = (field: keyof TaskListDefinition, value: string) => {
    onChange({ ...taskList, [field]: value });
  };

  const handleAddSection = () => {
    const label = newSectionLabel.trim();
    if (!label) return;

    const sectionId = slugify(label);
    const newSection: TaskSectionDefinition = {
      sectionId,
      label,
      tasks: [],
    };

    onChange({
      ...taskList,
      sections: [...taskList.sections, newSection],
    });
    setNewSectionLabel('');
  };

  const handleAddReference = () => {
    const ref: TaskListReference = {
      taskListId: referenceableTaskLists[0]?.taskListId ?? '',
    };
    onChange({
      ...taskList,
      sections: [...taskList.sections, ref],
    });
  };

  const handleSectionChange = (
    index: number,
    updated: TaskSectionDefinition | TaskListReference,
  ) => {
    const sections = [...taskList.sections];
    sections[index] = updated;
    onChange({ ...taskList, sections });
  };

  const handleSectionDelete = (index: number) => {
    const sections = taskList.sections.filter((_, i) => i !== index);
    onChange({ ...taskList, sections });
  };

  const handleSectionMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= taskList.sections.length) return;

    const sections = [...taskList.sections];
    [sections[index], sections[targetIndex]] = [sections[targetIndex], sections[index]];
    onChange({ ...taskList, sections });
  };

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarButton icon={IconArrowLeft} onClick={onBack}>
          Back to list
        </ToolbarButton>
        <Typography sx={{ ml: 0.5, fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
          {taskList.displayName}
        </Typography>
        <ToolbarSpacer />
        <ToolbarButton variant="primary" onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ ...CARD_SX, p: 2, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
            Task List Details
          </Typography>
          <FormField label="Display Name">
            <input
              type="text"
              value={taskList.displayName}
              onChange={(e) => handleFieldChange('displayName', e.target.value)}
            />
          </FormField>
          <FormField label="Description">
            <textarea
              rows={2}
              value={taskList.description ?? ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </FormField>
          <FormField label="Task List ID">
            <input type="text" value={taskList.taskListId} readOnly style={MONO} />
          </FormField>
          <div className="editor-hint">Set at creation and cannot be changed.</div>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '2px',
            pt: '4px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span className="editor-section-title">Sections ({taskList.sections.length})</span>
            <HelpHint
              text={
                <Box sx={{ maxWidth: 320 }}>
                  The ordered list of sections this task list contains. Each
                  entry is either:
                  <br />
                  <br />
                  <strong>Inline section:</strong> defined right here with its
                  own tasks. Use for content unique to this list.
                  <br />
                  <br />
                  <strong>Reference:</strong> points at another task list (an
                  "atomic" procedure like <em>wipe-windows</em>). The referenced
                  list is inlined at ticket-creation time, with any overrides
                  applied. Use to compose larger task lists from reusable
                  building blocks.
                </Box>
              }
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<IconPlus size={16} stroke={1.5} />}
            onClick={handleAddReference}
            disabled={referenceableTaskLists.length === 0}
            title="Add a reference to another task list. The referenced list's sections appear here at ticket time, in this order."
            sx={OUTLINED_BTN_SX}
          >
            Add Reference
          </Button>
        </Box>

        {taskList.sections.length === 0 && (
          <div className="editor-empty">
            No sections yet. Add a reference to an existing task list, or an
            inline section using the input below.
          </div>
        )}

        {(() => {
          // Every task defined in this list (inline sections only) — the
          // pickable sources for taskOutcome conditions (same-list scope).
          const listTasks = taskList.sections
            .filter((s) => !isTaskListReference(s))
            .flatMap((s) => (s as TaskSectionDefinition).tasks);
          return taskList.sections.map((section, index) => {
          const isRef = isTaskListReference(section);
          const key = isRef
            ? `ref-${index}-${section.taskListId}`
            : `inline-${section.sectionId}`;
          if (isRef) {
            return (
              <TaskListReferenceEditor
                key={key}
                reference={section}
                taskLists={referenceableTaskLists}
                onChange={(updated) => handleSectionChange(index, updated)}
                onDelete={() => handleSectionDelete(index)}
                onMoveUp={index > 0 ? () => handleSectionMove(index, 'up') : undefined}
                onMoveDown={
                  index < taskList.sections.length - 1
                    ? () => handleSectionMove(index, 'down')
                    : undefined
                }
              />
            );
          }
          return (
            <SectionEditor
              key={key}
              section={section}
              availableTasks={listTasks}
              onSectionChange={(updated) => handleSectionChange(index, updated)}
              onDelete={() => handleSectionDelete(index)}
              onMoveUp={index > 0 ? () => handleSectionMove(index, 'up') : undefined}
              onMoveDown={
                index < taskList.sections.length - 1
                  ? () => handleSectionMove(index, 'down')
                  : undefined
              }
            />
          );
          });
        })()}

        <Box sx={{ ...CARD_SX, p: 2, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Box sx={{ flex: 1 }}>
            <FormField label="New section label">
              <input
                type="text"
                value={newSectionLabel}
                onChange={(e) => setNewSectionLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSection();
                  }
                }}
              />
            </FormField>
            <div className="editor-hint" style={{ marginTop: 6 }}>
              Or use the Add Reference button above to compose from another task list.
            </div>
          </Box>
          <Button
            variant="outlined"
            startIcon={<IconPlus size={16} stroke={1.5} />}
            onClick={handleAddSection}
            disabled={!newSectionLabel.trim()}
            sx={[OUTLINED_BTN_SX, { mt: '4px', flexShrink: 0 }]}
          >
            Add Inline Section
          </Button>
        </Box>
      </Box>
    </>
  );
};
